/**
 * @fileoverview Payment Transaction Tracking Hooks
 * 
 * Provides hooks for querying and monitoring payment transactions throughout
 * their lifecycle. Payment transactions track the flow of funds from client
 * payment through escrow to vendor payout.
 * 
 * Transaction Lifecycle:
 * 1. payment_pending: Initial state, payment intent created
 * 2. ach_pending: ACH payment submitted, awaiting settlement (3-5 business days)
 * 3. payment_captured: Payment successfully charged to client
 * 4. held_in_escrow: Funds secured, awaiting job completion
 * 5. transfer_initiated: Transfer to vendor's Stripe account started
 * 6. transfer_complete: Funds successfully transferred to vendor
 * 7. refunded: Payment refunded to client (full or partial)
 * 8. failed: Payment attempt failed
 * 
 * Key Features:
 * - Track individual transaction status and history
 * - Support for both card and ACH payment methods
 * - Automatic retry logic for failed payments
 * - Detailed error tracking and logging
 * - Webhook-based status updates from Stripe
 * 
 * @module usePaymentTransactions
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Represents a payment transaction record in the system.
 * 
 * Each transaction tracks a single payment flow, including:
 * - Client payment capture
 * - Escrow holding period
 * - Vendor payout transfer
 * - Refund processing if needed
 * 
 * The transaction links to both the job and purchase order, and includes
 * references to Stripe objects (PaymentIntent and Transfer) for reconciliation.
 */
export interface PaymentTransaction {
  id: string;
  job_id: string;
  purchase_order_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_transfer_id: string | null;
  client_org_id: string;
  vendor_org_id: string;
  total_amount_cents: number;
  cc_processing_fee_cents: number; // CC fee charged to client (0 for ACH)
  platform_fee_cents: number;
  vendor_payout_cents: number;
  currency: string;
  status: 'payment_pending' | 'ach_pending' | 'payment_captured' | 'held_in_escrow' | 'transfer_initiated' | 'transfer_complete' | 'refunded' | 'failed' | 'invoice_pending' | 'voided';
  payment_captured_at: string | null;
  transfer_initiated_at: string | null;
  transfer_completed_at: string | null;
  payment_method_type: 'card' | 'us_bank_account' | 'stripe_invoice' | null;
  payment_method_last4: string | null;
  ach_settlement_expected_at: string | null;
  ach_settled_at: string | null;
  tax_amount_cents: number | null;
  vendor_tax_cents: number | null;
  metadata: any;
  error_message: string | null;
  retry_count: number;
  last_retry_at: string | null;
  next_retry_at: string | null;
  auto_retry_enabled: boolean;
  created_at: string;
  updated_at: string;
  // Invoice-specific fields
  invoice_status: 'open' | 'paid' | 'payment_failed' | 'void' | 'uncollectible' | null;
  invoice_paid_at: string | null;
  stripe_invoice_id: string | null;
  stripe_invoice_url: string | null;
}

/**
 * Hook for fetching a single payment transaction for a job
 * 
 * Retrieves the primary payment transaction for a job, excluding adjustment
 * transactions. This is typically the initial PO payment that moves through
 * the escrow and transfer lifecycle.
 * 
 * Filters out adjustment transactions (which have payment_context set) to
 * return only the main transaction for the job.
 * 
 * @param jobId - The UUID of the job
 * @returns Query object with the payment transaction or null if not found
 * 
 * @example
 * ```tsx
 * function PaymentStatusBadge({ jobId }) {
 *   const { data: transaction, isLoading } = usePaymentTransaction(jobId);
 * 
 *   if (isLoading) return <Spinner />;
 *   if (!transaction) return <span>No payment</span>;
 * 
 *   const statusColors = {
 *     payment_captured: 'green',
 *     ach_pending: 'yellow',
 *     held_in_escrow: 'blue',
 *     transfer_complete: 'green',
 *     failed: 'red'
 *   };
 * 
 *   return (
 *     <Badge color={statusColors[transaction.status]}>
 *       {transaction.status}
 *     </Badge>
 *   );
 * }
 * ```
 */
export const usePaymentTransaction = (jobId: string) => {
  return useQuery({
    queryKey: ['payment-transaction', jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('job_id', jobId)
        .is('payment_context', null)  // Only get initial PO payment, not adjustments
        .maybeSingle();

      if (error) throw error;
      return data as PaymentTransaction | null;
    },
    enabled: !!jobId,
  });
};

/**
 * Hook for fetching multiple payment transactions with optional filters
 * 
 * Retrieves a list of payment transactions ordered by creation date (most recent first).
 * Supports filtering by status, client organization, or vendor organization for
 * reporting and analytics purposes.
 * 
 * Common Filter Use Cases:
 * - status='held_in_escrow': Find all payments awaiting job completion
 * - status='failed': Identify failed payments requiring retry
 * - clientOrgId: View all payments from a specific client
 * - vendorOrgId: View all payouts to a specific vendor
 * 
 * @param filters - Optional filters for status, client org, and vendor org
 * @returns Query object with array of payment transactions
 * 
 * @example
 * ```tsx
 * function PaymentsList({ organizationId, userRole }) {
 *   const filters = userRole === 'client' 
 *     ? { clientOrgId: organizationId }
 *     : { vendorOrgId: organizationId };
 * 
 *   const { data: transactions, isLoading } = usePaymentTransactions(filters);
 * 
 *   return (
 *     <Table>
 *       {transactions?.map(tx => (
 *         <Row key={tx.id}>
 *           <Cell>${(tx.total_amount_cents / 100).toFixed(2)}</Cell>
 *           <Cell>{tx.status}</Cell>
 *           <Cell>{new Date(tx.created_at).toLocaleDateString()}</Cell>
 *         </Row>
 *       ))}
 *     </Table>
 *   );
 * }
 * ```
 */
export const usePaymentTransactions = (filters?: { 
  status?: string;
  clientOrgId?: string;
  vendorOrgId?: string;
  includeTestData?: boolean;
}) => {
  return useQuery({
    queryKey: ['payment-transactions', filters],
    queryFn: async () => {
      // Get test organization IDs to exclude if not including test data
      let testOrgIds: string[] = [];
      if (!filters?.includeTestData) {
        const { data: testOrgs } = await supabase
          .from('organizations')
          .select('id')
          .eq('is_test', true);
        testOrgIds = testOrgs?.map(o => o.id) || [];
      }

      let query = supabase
        .from('payment_transactions')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.clientOrgId) {
        query = query.eq('client_org_id', filters.clientOrgId);
      }
      if (filters?.vendorOrgId) {
        query = query.eq('vendor_org_id', filters.vendorOrgId);
      }

      // Exclude transactions from test organizations (both client and vendor)
      if (testOrgIds.length > 0) {
        query = query.not('client_org_id', 'in', `(${testOrgIds.join(',')})`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as PaymentTransaction[];
    },
  });
};
